import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useMobileLayout } from '@/utils/useMobileLayout';
import { cn } from '@/lib/utils';

interface MobileFormFooterProps {
  onSubmit?: () => void;
  submitType?: 'button' | 'submit';
  onCancel?: () => void;
  cancelTo?: string;
  isSubmitting?: boolean;
  saveDisabled?: boolean;
  saveLabel?: string;
  cancelLabel?: string;
  extraActions?: ReactNode;
  /** Render on desktop too — defaults to mobile-only. */
  alwaysVisible?: boolean;
  className?: string;
}

/**
 * Sticky action bar for forms. Defaults to mobile-only so desktop layouts
 * can keep their inline buttons.
 */
export function MobileFormFooter({
  onSubmit,
  submitType = 'submit',
  onCancel,
  cancelTo,
  isSubmitting,
  saveDisabled,
  saveLabel = 'Save',
  cancelLabel = 'Cancel',
  extraActions,
  alwaysVisible,
  className,
}: MobileFormFooterProps) {
  const navigate = useNavigate();
  const { isMobile } = useMobileLayout();

  if (!alwaysVisible && !isMobile) return null;

  const handleCancel = () => {
    if (onCancel) onCancel();
    else if (cancelTo) navigate(cancelTo);
    else navigate(-1);
  };

  return (
    <div
      className={cn(
        'fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80',
        alwaysVisible ? '' : 'md:hidden',
        className,
      )}
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    >
      <div className="container flex items-center gap-2 px-3 py-3">
        <Button
          type="button"
          variant="ghost"
          onClick={handleCancel}
          disabled={isSubmitting}
          className="flex-1"
        >
          {cancelLabel}
        </Button>
        {extraActions}
        <Button
          type={submitType}
          onClick={onSubmit}
          disabled={isSubmitting || saveDisabled}
          className="flex-1"
        >
          {isSubmitting ? 'Saving…' : saveLabel}
        </Button>
      </div>
    </div>
  );
}

export default MobileFormFooter;
